import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { initNativeShell } from "@/lib/native";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import { DeactivatedGate } from "@/components/DeactivatedGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Discover from "@/pages/Discover";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import BusinessOpportunities from "@/pages/BusinessOpportunities";
import CreateOpportunity from "@/pages/CreateOpportunity";
import OpportunityApprove from "@/pages/OpportunityApprove";
import MarketingStudio from "@/pages/MarketingStudio";
import OpportunityManage from "@/pages/OpportunityManage";
import CreatorProfilePublic from "@/pages/CreatorProfilePublic";
import BusinessProfilePublic from "@/pages/BusinessProfilePublic";
import PublicOpportunity from "@/pages/PublicOpportunity";
import InviteLanding from "@/pages/InviteLanding";
import CreatorEdit from "@/pages/CreatorEdit";
import MediaKit from "@/pages/MediaKit";
import MediaKitPublic from "@/pages/MediaKitPublic";
import BusinessEdit from "@/pages/BusinessEdit";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import Campaigns from "@/pages/Campaigns";
import MyNetwork from "@/pages/MyNetwork";
import Plans from "@/pages/Plans";
import CollaborationDetail from "@/pages/CollaborationDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import { CookieConsent } from "@/components/CookieConsent";
import { DeepLinkHandler } from "@/components/DeepLinkHandler";
import { TermsGate } from "@/components/TermsGate";
import { TermsUpdateNotice } from "@/components/TermsUpdateNotice";
import { EmailConsentPrompt } from "@/components/EmailConsentPrompt";

import About from "@/pages/info/About";
import HowItWorks from "@/pages/info/HowItWorks";
import FAQ from "@/pages/info/FAQ";
import Contact from "@/pages/info/Contact";
import CreatorGuidelines from "@/pages/info/CreatorGuidelines";
import BusinessGuidelines from "@/pages/info/BusinessGuidelines";
import Terms from "@/pages/info/Terms";
import Unsubscribe from "@/pages/Unsubscribe";
import Privacy from "@/pages/info/Privacy";
import Cookies from "@/pages/info/Cookies";
import CommunityGuidelines from "@/pages/info/CommunityGuidelines";
import Legal from "@/pages/info/Legal";

function App() {
  useEffect(() => { initNativeShell(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <EntitlementsProvider>
          <DeepLinkHandler />
          <TermsGate />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<AuthPage />} />
            <Route path="/reset-password" element={<AuthPage />} />
            <Route path="/activate" element={<AuthPage />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/opportunities" element={<ProtectedRoute><Opportunities /></ProtectedRoute>} />
            <Route path="/opportunities/:id" element={<ProtectedRoute><OpportunityDetail /></ProtectedRoute>} />
            <Route path="/business/opportunities" element={<ProtectedRoute roles={["business"]}><BusinessOpportunities /></ProtectedRoute>} />
            <Route path="/business/campaigns" element={<ProtectedRoute roles={["business"]}><Campaigns /></ProtectedRoute>} />
            <Route path="/business/opportunities/create" element={<ProtectedRoute roles={["business"]}><CreateOpportunity /></ProtectedRoute>} />
            <Route path="/business/opportunities/:id/edit" element={<ProtectedRoute roles={["business"]}><CreateOpportunity /></ProtectedRoute>} />
            <Route path="/business/opportunities/:id/review" element={<ProtectedRoute roles={["business"]}><OpportunityApprove /></ProtectedRoute>} />
            <Route path="/business/marketing" element={<ProtectedRoute roles={["business"]}><MarketingStudio /></ProtectedRoute>} />
            <Route path="/business/opportunities/:id" element={<ProtectedRoute roles={["business"]}><OpportunityManage /></ProtectedRoute>} />
            <Route path="/creators/:id" element={<CreatorProfilePublic />} />
            <Route path="/creator/:id" element={<CreatorProfilePublic />} />
            <Route path="/business/:id" element={<BusinessProfilePublic />} />
            <Route path="/opportunity/:id" element={<PublicOpportunity />} />
            <Route path="/invite/:code" element={<InviteLanding />} />

            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute roles={["creator", "business"]}><Analytics /></ProtectedRoute>} />
            <Route path="/network" element={<ProtectedRoute roles={["creator"]}><MyNetwork /></ProtectedRoute>} />
            <Route path="/plans" element={<ProtectedRoute roles={["creator", "business"]}><Plans /></ProtectedRoute>} />
            <Route path="/creator/profile" element={<ProtectedRoute roles={["creator"]}><CreatorEdit /></ProtectedRoute>} />
            <Route path="/creator/media-kit" element={<ProtectedRoute roles={["creator"]}><MediaKit /></ProtectedRoute>} />
            <Route path="/m/:shareId" element={<MediaKitPublic />} />
            <Route path="/business/profile" element={<ProtectedRoute roles={["business"]}><BusinessEdit /></ProtectedRoute>} />
            <Route path="/collaborations/:id" element={<ProtectedRoute><CollaborationDetail /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />

            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/creator-guidelines" element={<CreatorGuidelines />} />
            <Route path="/business-guidelines" element={<BusinessGuidelines />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/legal" element={<Legal />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <DeactivatedGate />
          <TermsUpdateNotice />
          <EmailConsentPrompt />
          <CookieConsent />
          <Toaster position="top-center" richColors />
          </EntitlementsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
